/**
 * One report, opened.
 *
 * Everything already in Slack stays in Slack and is shown read-only here — the description, the
 * reporter, the page, the thread. What this drawer adds is the two things a Slack thread cannot
 * hold: the decision, and a link to the fix. Screenshots are deliberately NOT copied over; they
 * can show candidate data and the Slack thread is the right boundary for that, so the drawer
 * counts them and links to the post.
 */
import { useEffect, useRef, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type {
  FeedbackPriority,
  FeedbackReport,
  FeedbackType,
  OwnerId,
  TriageOwner,
  TriagePatch,
  TriageStatus,
} from '../../services/feedbackTriageApi';
import { PiaChip, PriorityChip, TypeChip } from './triageUi';
import { PRIORITY_LABEL, RADIUS, STATUS_OPTIONS, ageOf } from './triageTokens';

const PRIORITIES: FeedbackPriority[] = ['low', 'medium', 'high', 'critical'];
const TYPES: FeedbackType[] = ['bug', 'improvement', 'feature', 'other'];

const FIELD =
  `w-full box-border ${RADIUS} border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 ` +
  'focus:outline-none focus:ring-2 focus:ring-blue-500';

const LABEL = 'block text-[10.5px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5';

interface Props {
  report: FeedbackReport;
  owners: TriageOwner[];
  busy: boolean;
  onClose: () => void;
  onSave: (patch: TriagePatch) => void;
}

export function ReportDetail({ report, owners, busy, onClose, onSave }: Props) {
  const [decision, setDecision] = useState(report.decision);
  const [fixUrl, setFixUrl] = useState(report.fixUrl);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Focus on open, so Escape and Tab start somewhere sensible. There is deliberately no effect
  // re-seeding the two fields from props: the parent keys this component on the report's ts, so a
  // different report is a different component and an unsaved note cannot follow the reader onto
  // somebody else's bug.
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dirty = decision !== report.decision || fixUrl !== report.fixUrl;
  const reclassified = report.priority !== report.reportedPriority || report.type !== report.reportedType;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* A plain button, so the backdrop is reachable by keyboard as well as by pointer. */}
      <button
        type="button"
        aria-label="Close report"
        className="absolute inset-0 bg-zinc-900/20"
        onClick={onClose}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={report.title}
        className="relative flex h-full w-[min(760px,92vw)] flex-col border-l border-zinc-200 bg-white shadow-xl"
      >
        <header className="border-b border-zinc-100 px-6 py-5">
          <div className="flex items-center gap-2.5">
            <PriorityChip priority={report.priority} />
            <TypeChip type={report.type} />
            {reclassified && (
              <span className="text-xs text-zinc-400">
                filed as {PRIORITY_LABEL[report.reportedPriority]} · {report.reportedType}
              </span>
            )}
            <span className="text-xs text-zinc-500">{report.area}</span>
            <div className="flex-1" />
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`inline-flex size-8 items-center justify-center ${RADIUS} border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900`}
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <h2 className="mt-3 text-xl font-bold leading-snug tracking-tight text-zinc-900">
            {report.title}
          </h2>
          {/* The reporter's own words, as text. A bug report is untrusted input like any other. */}
          <p className="mt-2.5 whitespace-pre-wrap text-sm leading-relaxed text-zinc-700">
            {report.description}
          </p>
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          <dl className="grid grid-cols-2 gap-x-7 gap-y-2 text-[12.5px]">
            <Fact label="Reported by" value={report.reporter || 'unknown'} />
            <Fact
              label="Reported"
              value={`${new Date(report.postedAt).toLocaleDateString()} · ${ageOf(report.postedAt)} ago`}
            />
            <Fact label="Page" value={report.pagePath || '—'} mono />
            <Fact
              label="In the thread"
              value={[
                report.replies ? `${report.replies} ${report.replies === 1 ? 'reply' : 'replies'}` : null,
                report.screenshots
                  ? `${report.screenshots} screenshot${report.screenshots > 1 ? 's' : ''}`
                  : null,
                report.hasCandidateContext ? 'a candidate message' : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'nothing'}
            />
          </dl>

          {report.permalink && (
            <a
              href={report.permalink}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex w-fit items-center gap-2 ${RADIUS} border border-zinc-200 px-3 py-2 text-[12.5px] font-semibold text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900`}
            >
              Read the thread in Slack
              <ExternalLink className="size-3.5" aria-hidden />
            </a>
          )}

          {/*
            The reporter picked priority and type when filing; an owner corrects them here. The
            correction is what the weekly round-up and Pia's next round go by — "as filed" clears it.
          */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL} htmlFor="triage-priority">
                Priority
              </label>
              <select
                id="triage-priority"
                className={FIELD}
                value={report.priority === report.reportedPriority ? '' : report.priority}
                disabled={busy}
                onChange={(e) => onSave({ priority: (e.target.value || null) as FeedbackPriority | null })}
              >
                <option value="">As filed · {PRIORITY_LABEL[report.reportedPriority]}</option>
                {PRIORITIES.filter((p) => p !== report.reportedPriority).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABEL[p]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL} htmlFor="triage-type">
                Type
              </label>
              <select
                id="triage-type"
                className={`${FIELD} capitalize`}
                value={report.type === report.reportedType ? '' : report.type}
                disabled={busy}
                onChange={(e) => onSave({ type: (e.target.value || null) as FeedbackType | null })}
              >
                <option value="">As filed · {report.reportedType}</option>
                {TYPES.filter((t) => t !== report.reportedType).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {report.pia && (
            <div className={`flex flex-wrap items-center gap-3 border border-zinc-200 bg-zinc-50 px-3 py-2.5 ${RADIUS}`}>
              <PiaChip pia={report.pia} />
              <span className="text-[12.5px] text-zinc-600">
                {report.pia.rounds > 0 && `${report.pia.rounds} follow-up ${report.pia.rounds === 1 ? 'round' : 'rounds'}`}
                {report.pia.riskLevel && ` · risk ${report.pia.riskLevel}`}
                {report.pia.at && ` · ${new Date(report.pia.at).toLocaleString()}`}
              </span>
              {report.pia.prUrl && (
                <a
                  href={report.pia.prUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-zinc-700 hover:text-zinc-900 hover:underline"
                >
                  Open PR #{report.pia.prNumber}
                  <ExternalLink className="size-3" aria-hidden />
                </a>
              )}
            </div>
          )}

          <div>
            <label className={LABEL} htmlFor="triage-decision">
              What we decided
            </label>
            <textarea
              id="triage-decision"
              rows={3}
              className={`${FIELD} resize-y leading-relaxed`}
              placeholder="One line — this is what gets read out in the hiring huddle."
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
            />
          </div>

          <div>
            <label className={LABEL} htmlFor="triage-fix">
              Where the fix lives
            </label>
            <input
              id="triage-fix"
              type="text"
              className={FIELD}
              placeholder="finn-auto/people-space#412"
              value={fixUrl}
              onChange={(e) => setFixUrl(e.target.value)}
            />
          </div>

          {report.updatedAt && (
            <p className="text-[11.5px] text-zinc-400">
              Last changed by {report.updatedBy || 'someone'} on{' '}
              {new Date(report.updatedAt).toLocaleString()}.
            </p>
          )}
        </div>

        <footer className="flex items-end gap-3 border-t border-zinc-200 bg-zinc-50 px-6 py-4">
          <div className="w-44">
            <label className={LABEL} htmlFor="triage-owner">
              Owner
            </label>
            <select
              id="triage-owner"
              className={FIELD}
              value={report.owner}
              disabled={busy}
              onChange={(e) => onSave({ owner: e.target.value as OwnerId })}
            >
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <label className={LABEL} htmlFor="triage-status">
              Status
            </label>
            <select
              id="triage-status"
              className={FIELD}
              value={report.status}
              disabled={busy}
              onChange={(e) => onSave({ status: e.target.value as TriageStatus })}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1" />
          <Button
            variant="primary"
            disabled={busy || !dirty}
            onClick={() => onSave({ decision, fixUrl })}
          >
            {dirty ? 'Save' : 'Saved'}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-3">
      <dt className="w-24 shrink-0 text-zinc-400">{label}</dt>
      <dd className={`m-0 min-w-0 break-words text-zinc-700 ${mono ? 'font-mono text-[11.5px]' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
